import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Grid, Typography, TextField, Button } from '@material-ui/core';
import {
  useFormik,
  FormikHelpers,
  getIn,
  FormikProvider,
  FieldArray,
  FieldArrayRenderProps,
} from 'formik';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { ApplicationState } from '../../stores';
import { useStyles } from './style';
import SelectField from '../../components/selectField/selectField';
import { PersonRole } from '../../models/eums/personRole';
import { inviteUserReq } from '../../api/registrationApi';
import { emptyUserInvitationObj } from '../../models/userInvitationModel';
import { validationSchema } from './validation';
import { UserInvitationModel } from '../../models/userInvitationModel';
import { toPropperCase } from '../../utils/stringUtils';
import { DatePicker } from '../../components/datePicker';
import moment from 'moment';
import { PersonCertificationTable } from '../../components/personCertificationTable/personCertificationTable';
import { CertificationModel } from '../../models/certificationModel';

function InviteUser() {
  const inviteUserStyles = useStyles();
  const { t } = useTranslation();
  const formikBag = useFormik({
    initialValues: emptyUserInvitationObj,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: submit,
  });

  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    submitForm,
  } = formikBag;
  const isLoading = useSelector<ApplicationState, boolean>(state => state.loader.loading);

  async function submit(values: UserInvitationModel, actions: FormikHelpers<UserInvitationModel>) {
    actions.resetForm();
    await inviteUserReq(values);
  }

  function addCertification(newCert: CertificationModel, arrayHelpers: FieldArrayRenderProps) {
    return new Promise(resolve => {
      arrayHelpers.push(newCert);
      resolve();
    });
  }

  function editCertification(
    newCert: CertificationModel,
    arrayHelpers: FieldArrayRenderProps,
    oldCert?: CertificationModel,
  ) {
    return new Promise((resolve, reject) => {
      if (!oldCert) {
        reject();
        return;
      }

      const certIndex = values.certifications.indexOf(oldCert);
      arrayHelpers.replace(certIndex, newCert);
      resolve();
    });
  }

  function deleteCertification(cert: CertificationModel, arrayHelpers: FieldArrayRenderProps) {
    return new Promise(resolve => {
      const certIndex = values.certifications.indexOf(cert);
      arrayHelpers.remove(certIndex);
      resolve();
    });
  }

  function getHelperText(fieldPath: string) {
    if (getIn(touched, fieldPath)) return getIn(errors, fieldPath) || '';

    return '';
  }

  function isFieldError(fieldPath: string) {
    return getIn(touched, fieldPath) && !!getIn(errors, fieldPath);
  }

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <Typography variant='h5'>{t('invite_user')}</Typography>
      </Grid>

      <Grid container item direction='column' spacing={2} xs={12} md={6}>
        <Grid item>
          <TextField
            error={isFieldError('emailAddress')}
            variant='outlined'
            margin='normal'
            required
            name='emailAddress'
            label={t('email_address')}
            className={inviteUserStyles.field}
            value={values.emailAddress || ''}
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={getHelperText('emailAddress')}
          />
        </Grid>
        <Grid item>
          <SelectField
            name='user_role'
            inputLabel={t('user_role')}
            options={Object.values(PersonRole)}
            value={toPropperCase(values.userRole)}
            className={inviteUserStyles.field}
            onInputChange={value => {
              setFieldValue('userRole', value);
            }}
          />
        </Grid>
        <Grid item>
          <DatePicker
            required
            error={isFieldError('registrationValidity')}
            onBlur={ev => setFieldTouched('registrationValidity')}
            onOpen={() => setFieldTouched('registrationValidity')}
            margin='normal'
            id='date-picker-end'
            variant='inline'
            inputVariant='outlined'
            label={t('registration_validity')}
            format='dd.MM.yyyy.'
            value={values.registrationValidity || null}
            helperText={t(getHelperText('registrationValidity'))}
            onChange={(date, val) => {
              if (val) {
                const mDate = moment.utc(val, 'DD.MM.YYYY.', true);
                setFieldValue('registrationValidity', mDate.toDate().toUTCString(), true);
              } else setFieldValue('registrationValidity', undefined, true);
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormikProvider value={formikBag}>
          <FieldArray name='certifications'>
            {arrayHelpers => (
              <PersonCertificationTable
                data={values.certifications}
                onRowAdd={newCert => addCertification(newCert, arrayHelpers)}
                onRowDelete={cert => deleteCertification(cert, arrayHelpers)}
                onRowUpdate={(newCert, oldCert) =>
                  editCertification(newCert, arrayHelpers, oldCert)
                }
              />
            )}
          </FieldArray>
        </FormikProvider>
      </Grid>
      <Grid item>
        <Button
          type='submit'
          variant='contained'
          color='primary'
          size='large'
          startIcon={<PersonAddIcon />}
          disabled={!values.emailAddress || isLoading}
          onClick={submitForm}
        >
          {t('invite')}
        </Button>
      </Grid>
    </Grid>
  );
}

export default InviteUser;
